import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@dishfolio/ui";
import { api, errorMessage } from "../lib/api.ts";
import { AuthLayout } from "./auth.tsx";
import { FullPageSpinner } from "../components/Spinner.tsx";

export function AcceptInvitePage() {
  const { token } = useParams({ from: "/invite/$token" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invite = useQuery({ queryKey: ["invite", token], queryFn: () => api.get<{ email: string; role: string; organizationName: string }>(`/invitations/${token}`), retry: false });
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.get<{ user: { email: string } }>("/me"), retry: false });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  if (invite.isPending || me.isPending) return <FullPageSpinner />;
  if (invite.error) return <AuthLayout title="Invitation not found" subtitle="This invitation was revoked, already used, or has expired." />;
  const signedIn = !me.error;
  const redirect = `/invite/${token}`;
  return (
    <AuthLayout title={`Join ${invite.data.organizationName}`} subtitle={`You’ve been invited as ${invite.data.role} (${invite.data.email}).`}>
      {!signedIn ? (
        <div className="grid gap-2">
          <Button asChild>
            <Link to="/signup" search={{ redirect }}>
              Create an account
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/login" search={{ redirect }}>
              I already have an account
            </Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button
            loading={loading}
            onClick={async () => {
              setLoading(true);
              try {
                const res = await api.post<{ organizationId: string }>(`/invitations/${token}/accept`);
                localStorage.setItem("dishfolio.org", res.organizationId);
                await qc.invalidateQueries();
                await navigate({ to: "/" });
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setLoading(false);
              }
            }}
          >
            Accept invitation
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
